/**
 * Un lado de una corrección (lo que dijiste, o la forma corregida) con las
 * palabras que cambiaron resaltadas.
 *
 * El estilo es un RESALTADOR (chip de color detrás de la palabra) — el mismo
 * recurso que ya usa la palabra foco de Órbitas, que se eligió mirando las
 * alternativas en la app. El color lo decide quien llama vía `color`, así el
 * chip queda en el matiz que esa superficie ya usa para ese lado (la severidad
 * del lado "dijiste", el lima del lado corregido) en vez de sumar un color más.
 *
 * Sin marcas devuelve el texto pelado: una superficie que no resuelve el diff
 * renderiza exactamente como antes.
 */
import { useMemo, type CSSProperties } from 'react';
import { groupRuns } from '../../lib/tokenDiff.js';
import './correction-diff.css';

interface Props {
  readonly text: string;
  /** Índices de los tokens a marcar — `said` o `fix` de `CorrectionMarks`. */
  readonly marks: ReadonlySet<number>;
  /** Color del resaltado (cualquier color CSS; típicamente un token o el acento de severidad). */
  readonly color: string;
}

/**
 * Corta el texto en palabras junto con el separador que sigue a cada una. El
 * separador se guarda en vez de reponer un espacio fijo para devolver el texto
 * EXACTO: un doble espacio o un salto de línea del usuario se conserva.
 *
 * La lista de palabras coincide token a token con la de `tokenizeText`, que es
 * la que indexan las marcas.
 */
function splitWithGaps(text: string): Array<{ word: string; gap: string }> {
  const parts: Array<{ word: string; gap: string }> = [];
  const re = /(\S+)(\s*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) parts.push({ word: m[1] ?? '', gap: m[2] ?? '' });
  return parts;
}

export function DiffText({ text, marks, color }: Props) {
  const parts = useMemo(() => splitWithGaps(text), [text]);
  const runs = useMemo(() => groupRuns(parts.length, (i) => marks.has(i)), [parts.length, marks]);
  if (marks.size === 0) return <>{text}</>;
  return (
    <span className="cdiff" style={{ '--cdiff-c': color } as CSSProperties}>
      {runs.map((run) => {
        // Los espacios INTERNOS del tramo van adentro del chip (dos palabras
        // corregidas seguidas salen como UN bloque, no como cajitas sueltas);
        // el que cierra el tramo va afuera, o el fondo se estira un blanco más.
        const slice = parts.slice(run.start, run.end);
        const body = slice.map((p, k) => (k < slice.length - 1 ? p.word + p.gap : p.word)).join('');
        const tail = slice[slice.length - 1]?.gap ?? '';
        return (
          <span key={run.start}>
            {run.target ? <mark className="cdiff-mark">{body}</mark> : body}
            {tail}
          </span>
        );
      })}
    </span>
  );
}
